import { useState, useMemo } from 'react';
import { mockCapacitySignals } from '@/mocks/capacity';
import { mockOffers } from '@/mocks/offers';
import { mockPartners } from '@/mocks/partners';
import { mockVerticals } from '@/mocks/verticals';
import { mockGeos } from '@/mocks/geos';
import { Save, AlertTriangle } from 'lucide-react';

const urgencyColors: Record<string, { bg: string; color: string; border: string }> = {
  low: { bg: 'var(--success-bg)', color: 'var(--success-text)', border: 'var(--success-border)' },
  medium: { bg: 'var(--warning-bg)', color: 'var(--warning-text)', border: 'var(--warning-border)' },
  high: { bg: 'var(--danger-bg)', color: 'var(--danger-text)', border: 'var(--danger-border)' },
};

export default function CapacityPage() {
  const [signals, setSignals] = useState(mockCapacitySignals);

  const grouped = useMemo(() => {
    const byOffer: Record<string, typeof signals> = {};
    signals.forEach(s => {
      if (!byOffer[s.offer_id]) byOffer[s.offer_id] = [];
      byOffer[s.offer_id].push(s);
    });
    return Object.entries(byOffer).map(([offerId, items]) => {
      const offer = mockOffers.find(o => o.id === offerId);
      const partner = mockPartners.find(p => p.id === offer?.partner_id);
      const vertical = mockVerticals.find(v => v.id === offer?.vertical_id);
      const geo = mockGeos.find(g => g.id === offer?.geo_id);
      return {
        offerId,
        name: `${partner?.display_name || offer?.partner_id} — ${vertical?.name || ''} · ${geo?.name || ''}`,
        items: items.sort((a, b) => a.display_order - b.display_order),
      };
    });
  }, [signals]);

  const updateSignal = (offerId: string, serviceKey: string, field: string, value: unknown) => {
    setSignals(prev => prev.map(s =>
      s.offer_id === offerId && s.service_key === serviceKey
        ? { ...s, [field]: value, updated_at: new Date().toISOString() }
        : s
    ));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>Capacity / Wachttijd</h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--text-tertiary)' }}>Real-time load monitoring per offer</p>
        </div>
        <button className="flex items-center gap-1.5 px-3 py-2 text-sm rounded-xl transition-all"
          style={{ background: 'var(--accent)', color: 'var(--text-inverse)' }}
        >
          <Save size={14} />
          Save Changes
        </button>
      </div>

      <div className="space-y-4">
        {grouped.map(group => (
          <div key={group.offerId} className="rounded-xl border overflow-hidden"
            style={{ background: 'var(--bg-surface-raised)', borderColor: 'var(--border-primary)' }}
          >
            <div className="px-5 py-3.5 border-b" style={{ borderColor: 'var(--border-separator)' }}>
              <h2 className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>{group.name}</h2>
            </div>
            <div>
              {group.items.map((item, ii) => (
                <div key={item.service_key}
                  className="px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6 transition-colors hover-surface"
                  style={{ borderBottom: ii < group.items.length - 1 ? '1px solid var(--border-separator)' : undefined }}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{item.display_label}</p>
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border"
                        style={urgencyColors[item.urgency]}
                      >{item.urgency}</span>
                    </div>
                    {item.note && <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{item.note}</p>}
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <label className="text-xs" style={{ color: 'var(--text-tertiary)' }}>Load</label>
                      <input
                        type="number"
                        value={item.current_load}
                        onChange={e => updateSignal(item.offer_id, item.service_key, 'current_load', parseInt(e.target.value) || 0)}
                        className="w-16 px-2 py-1 text-sm rounded-lg outline-none transition-all text-center"
                        style={{ background: 'var(--bg-input)', border: '1px solid var(--border-primary)', color: 'var(--text-secondary)' }}
                      />
                      <span className="text-xs" style={{ color: 'var(--text-muted)' }}>/</span>
                      <input
                        type="number"
                        value={item.max_load || ''}
                        onChange={e => updateSignal(item.offer_id, item.service_key, 'max_load', e.target.value ? parseInt(e.target.value) : null)}
                        placeholder="∞"
                        className="w-16 px-2 py-1 text-sm rounded-lg outline-none transition-all text-center"
                        style={{ background: 'var(--bg-input)', border: '1px solid var(--border-primary)', color: 'var(--text-secondary)' }}
                      />
                    </div>
                    <div className="w-24">
                      <div className="w-full h-[3px] rounded-full overflow-hidden" style={{ background: 'var(--bg-input)' }}>
                        <div
                          className="h-full rounded-full transition-all duration-500"
                          style={{
                            width: `${Math.min(item.max_load ? (item.current_load / item.max_load) * 100 : 0, 100)}%`,
                            background: (item.max_load ? (item.current_load / item.max_load) : 0) >= 1
                              ? 'var(--danger-dot)'
                              : (item.max_load ? (item.current_load / item.max_load) : 0) >= 0.7
                                ? 'var(--warning-dot)'
                                : 'var(--accent)',
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}